import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { User, UserPreferences, Tweet, IntervalDigest, DailyLesson } from '@/types';

interface AuthState {
  user: User | null;
  token: string | null;
  isAuthenticated: boolean;
  setAuth: (user: User, token: string) => void;
  clearAuth: () => void;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      user: null,
      token: null,
      isAuthenticated: false,
      setAuth: (user, token) => {
        console.log('[store] setAuth called, token:', token.substring(0, 20) + '...');
        localStorage.setItem('token', token);
        set({ user, token, isAuthenticated: true });
      },
      clearAuth: () => {
        console.log('[store] clearAuth called');
        localStorage.removeItem('token');
        set({ user: null, token: null, isAuthenticated: false });
      },
    }),
    {
      name: 'auth-storage',
      partialize: (state) => ({ user: state.user, token: state.token, isAuthenticated: state.isAuthenticated }),
    }
  )
);

// Log initial state
console.log('[store] Initial state, isAuthenticated:', useAuthStore.getState().isAuthenticated, 
            'token exists:', !!useAuthStore.getState().token);

// If we have a token in localStorage but not in store, sync it
if (typeof window !== 'undefined') {
  const storedToken = localStorage.getItem('token');
  const storeToken = useAuthStore.getState().token;
  console.log('[store] localStorage token:', storedToken ? 'exists' : 'null', 
              'store token:', storeToken ? 'exists' : 'null');
}

interface FeedState {
  tweets: Tweet[];
  filter: {
    category: 'all' | 'news' | 'opinion' | 'analysis' | 'humor' | 'discussion' | 'promotion' | 'other';
    timeWindow: number;
    sentiment?: string;
    urgency?: string;
    hasMedia?: boolean;
    isInformative?: boolean;
    isControversial?: boolean;
    topics?: string[];
  };
  isLoading: boolean;
  hasMore: boolean;
  page: number;
  setTweets: (tweets: Tweet[]) => void;
  addTweets: (tweets: Tweet[]) => void;
  prependTweets: (tweets: Tweet[]) => void;
  updateTweet: (id: number, updates: Partial<Tweet>) => void;
  setFilter: (filter: Partial<FeedState['filter']>) => void;
  setLoading: (loading: boolean) => void;
  setHasMore: (hasMore: boolean) => void;
  setPage: (page: number) => void;
  reset: () => void;
}

export const useFeedStore = create<FeedState>((set) => ({
  tweets: [],
  filter: {
    category: 'all',
    timeWindow: 24,
  },
  isLoading: false,
  hasMore: true,
  page: 1,
  setTweets: (tweets) => set({ tweets }),
  addTweets: (tweets) => set((state) => ({ tweets: [...state.tweets, ...tweets] })),
  prependTweets: (tweets) => set((state) => ({ 
    tweets: [...tweets.map(t => ({ ...t, isNew: true })), ...state.tweets] 
  })),
  updateTweet: (id, updates) => set((state) => ({
    tweets: state.tweets.map(t => t.id === id ? { ...t, ...updates } : t)
  })),
  setFilter: (filter) => set((state) => ({ filter: { ...state.filter, ...filter } })),
  setLoading: (isLoading) => set({ isLoading }),
  setHasMore: (hasMore) => set({ hasMore }),
  setPage: (page) => set({ page }),
  reset: () => set({ tweets: [], page: 1, hasMore: true }),
}));

interface PreferencesState {
  preferences: UserPreferences | null;
  isLoading: boolean;
  setPreferences: (preferences: UserPreferences) => void;
  setLoading: (loading: boolean) => void;
}

export const usePreferencesStore = create<PreferencesState>((set) => ({
  preferences: null,
  isLoading: false,
  setPreferences: (preferences) => set({ preferences }),
  setLoading: (isLoading) => set({ isLoading }),
}));

interface ContentState {
  digests: IntervalDigest[];
  lessons: DailyLesson[];
  selectedDigest: IntervalDigest | null;
  selectedLesson: DailyLesson | null;
  isLoading: boolean;
  setDigests: (digests: IntervalDigest[]) => void;
  setLessons: (lessons: DailyLesson[]) => void;
  setSelectedDigest: (digest: IntervalDigest | null) => void;
  setSelectedLesson: (lesson: DailyLesson | null) => void;
  setLoading: (loading: boolean) => void;
}

export const useContentStore = create<ContentState>((set) => ({
  digests: [],
  lessons: [],
  selectedDigest: null,
  selectedLesson: null,
  isLoading: false,
  setDigests: (digests) => set({ digests }),
  setLessons: (lessons) => set({ lessons }),
  setSelectedDigest: (selectedDigest) => set({ selectedDigest }),
  setSelectedLesson: (selectedLesson) => set({ selectedLesson }),
  setLoading: (isLoading) => set({ isLoading }),
}));

interface UIState {
  sidebarOpen: boolean;
  theme: 'light' | 'dark' | 'system';
  toasts: Array<{
    id: string;
    title?: string;
    description?: string;
    variant?: 'default' | 'destructive';
  }>;
  toggleSidebar: () => void;
  setTheme: (theme: UIState['theme']) => void;
  addToast: (toast: Omit<UIState['toasts'][0], 'id'>) => void;
  removeToast: (id: string) => void;
}

export const useUIStore = create<UIState>((set) => ({
  sidebarOpen: true,
  theme: 'system',
  toasts: [],
  toggleSidebar: () => set((state) => ({ sidebarOpen: !state.sidebarOpen })),
  setTheme: (theme) => set({ theme }),
  addToast: (toast) =>
    set((state) => ({
      toasts: [...state.toasts, { ...toast, id: Math.random().toString(36).substring(2, 9) }],
    })),
  removeToast: (id) =>
    set((state) => ({
      toasts: state.toasts.filter((t) => t.id !== id),
    })),
}));

// New: Media viewer state
interface MediaViewerState {
  isOpen: boolean;
  mediaItems: Array<{
    url: string;
    type: 'image' | 'video';
    description?: string;
  }>;
  currentIndex: number;
  openViewer: (items: MediaViewerState['mediaItems'], startIndex?: number) => void;
  closeViewer: () => void;
  goToNext: () => void;
  goToPrevious: () => void;
  setIndex: (index: number) => void;
}

export const useMediaViewerStore = create<MediaViewerState>((set) => ({
  isOpen: false,
  mediaItems: [],
  currentIndex: 0,
  openViewer: (items, startIndex = 0) => set({ 
    isOpen: true, 
    mediaItems: items, 
    currentIndex: startIndex 
  }),
  closeViewer: () => set({ isOpen: false, mediaItems: [], currentIndex: 0 }),
  goToNext: () => set((state) => ({ 
    currentIndex: (state.currentIndex + 1) % state.mediaItems.length 
  })),
  goToPrevious: () => set((state) => ({ 
    currentIndex: state.currentIndex === 0 
      ? state.mediaItems.length - 1 
      : state.currentIndex - 1 
  })),
  setIndex: (index) => set({ currentIndex: index }),
}));
